import type {
	BaseBehaviorEventPayload,
	BehaviorPropertySnapshot,
	BehaviorPropertySnapshotSource,
	TrackLeadCreatedInput,
	TrackPropertyViewedInput,
	TrackWishlistBehaviorInput,
	UseBehaviorEventTrackingReturn
} from './types';

import { useLogger } from '~/composables/useLogger';
import { useTrackingIdentity } from '~/composables/visitorIdentity';
import { v4 as uuidv4 } from 'uuid';

import { buildPropertySnapshot } from './buildPropertySnapshot';
import { fetchPropertySnapshotSource } from './fetchPropertySnapshotSource';
import {
	hasOfflineBehaviorViewForProperty,
	removeOfflineBehaviorViewsForProperty,
	saveBehaviorViewOffline,
	takeOfflineBehaviorViews
} from './offlineStorage';
import {
	hasViewedPropertyThisSession,
	markPropertyViewedThisSession,
	unmarkPropertyViewedThisSession
} from './sessionDedup';

const ENDPOINT = '/api/behavior-events';

function resolveSnapshot(
	property?: BehaviorPropertySnapshotSource | null
): BehaviorPropertySnapshot | undefined {
	if (!property) return undefined;
	return buildPropertySnapshot(property);
}

/** Returns false when the event is dropped. */
function sendEvent(
	payload: BaseBehaviorEventPayload,
	log: ReturnType<typeof useLogger>
): boolean {
	if (!payload.propertySnapshot) {
		log.warn('Event dropped: missing required property_snapshot', {
			eventType: payload.eventType,
			propertyId: payload.propertyId
		});
		return false;
	}

	log.debug('Emitting behavior event', {
		eventId: payload.eventId,
		eventType: payload.eventType,
		propertyId: payload.propertyId,
		sessionId: payload.sessionId
	});

	try {
		fetch(ENDPOINT, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload),
			keepalive: true
		}).catch(() => {
			/* fire-and-forget — tracking failures must never reach the user */
		});
	} catch {
		/* fetch may throw synchronously on invalid input — also swallow */
	}
	return true;
}

function buildCommonFields(
	eventType: BaseBehaviorEventPayload['eventType'],
	portalUserId: string,
	sessionId: string,
	propertyId: string,
	propertySnapshot?: BehaviorPropertySnapshot,
	occurredAt?: string
): Omit<BaseBehaviorEventPayload, 'payload'> {
	return {
		eventId: uuidv4(),
		eventType,
		occurredAt: occurredAt ?? new Date().toISOString(),
		sessionId,
		portalUserId,
		propertyId,
		...(propertySnapshot ? { propertySnapshot } : {})
	};
}

export function useBehaviorEventTracking(): UseBehaviorEventTrackingReturn {
	const getTrackingIdentity = useTrackingIdentity();
	const { user, loggedIn } = useUserSession();
	const logger = useLogger('BehaviorEventTracking');

	// Shared by events fired together (property_viewed + wishlist_property_added)
	// so one missing property costs one lookup.
	const pendingSnapshotLookups = new Map<
		string,
		Promise<BehaviorPropertySnapshot | undefined>
	>();

	function getPortalUserId(): string | null {
		return user.value?.id ?? null;
	}

	function lookupSnapshot(
		propertyId: string
	): Promise<BehaviorPropertySnapshot | undefined> {
		const pending = pendingSnapshotLookups.get(propertyId);
		if (pending) return pending;

		const lookup = fetchPropertySnapshotSource(propertyId)
			.then(result => {
				if (!result) {
					logger.warn('Property lookup for behavior snapshot failed', {
						propertyId
					});
					return undefined;
				}
				return buildPropertySnapshot(result.property);
			})
			.finally(() => {
				pendingSnapshotLookups.delete(propertyId);
			});
		pendingSnapshotLookups.set(propertyId, lookup);
		return lookup;
	}

	/**
	 * Sends the event, first fetching the property when the caller had none.
	 * sendEvent still drops it if the lookup fails; onDropped then runs.
	 */
	function emitEvent(
		event: BaseBehaviorEventPayload,
		onDropped?: () => void
	): void {
		if (event.propertySnapshot) {
			sendEvent(event, logger);
			return;
		}

		void lookupSnapshot(event.propertyId)
			.then(propertySnapshot => {
				const sent = sendEvent(
					propertySnapshot ? { ...event, propertySnapshot } : event,
					logger
				);
				if (!sent) onDropped?.();
			})
			.catch((error: unknown) => {
				onDropped?.();
				// Tracking must never surface as an unhandled rejection
				logger.warn('Behavior event dropped after snapshot lookup error', {
					eventType: event.eventType,
					propertyId: event.propertyId,
					error: error instanceof Error ? error.message : String(error)
				});
			});
	}

	function trackPropertyViewed(input: TrackPropertyViewedInput): void {
		if (!import.meta.client) return;
		if (!input.propertyId) return;
		if (hasViewedPropertyThisSession(input.propertyId)) return;

		const viewType = input.viewType ?? 'detail_page';
		const source = input.source ?? 'property_page';
		const propertySnapshot = resolveSnapshot(input.property);
		const { sessionId } = getTrackingIdentity();
		const occurredAt = new Date().toISOString();
		const portalUserId = getPortalUserId();
		if (!loggedIn.value || !portalUserId) {
			if (hasOfflineBehaviorViewForProperty(input.propertyId)) return;
			saveBehaviorViewOffline({
				propertyId: input.propertyId,
				viewType,
				source,
				sessionId,
				occurredAt,
				propertySnapshot
			});
			return;
		}

		removeOfflineBehaviorViewsForProperty(input.propertyId);
		// Marked before the lookup so events fired together stay deduplicated
		markPropertyViewedThisSession(input.propertyId);

		emitEvent(
			{
				...buildCommonFields(
					'property_viewed',
					portalUserId,
					sessionId,
					input.propertyId,
					propertySnapshot,
					occurredAt
				),
				payload: { viewType, source }
			},
			() => unmarkPropertyViewedThisSession(input.propertyId)
		);
	}

	function trackWishlistPropertyAdded(input: TrackWishlistBehaviorInput): void {
		if (!import.meta.client) return;
		const portalUserId = getPortalUserId();
		if (!portalUserId || !input.wishlistId || !input.propertyId) return;

		trackPropertyViewed({
			propertyId: input.propertyId,
			property: input.property
		});

		const { sessionId } = getTrackingIdentity();
		emitEvent({
			...buildCommonFields(
				'wishlist_property_added',
				portalUserId,
				sessionId,
				input.propertyId,
				resolveSnapshot(input.property)
			),
			payload: { wishlistId: input.wishlistId }
		});
	}

	function trackWishlistPropertyRemoved(
		input: TrackWishlistBehaviorInput
	): void {
		if (!import.meta.client) return;
		const portalUserId = getPortalUserId();
		if (!portalUserId || !input.wishlistId || !input.propertyId) return;

		const { sessionId } = getTrackingIdentity();
		emitEvent({
			...buildCommonFields(
				'wishlist_property_removed',
				portalUserId,
				sessionId,
				input.propertyId,
				resolveSnapshot(input.property)
			),
			payload: { wishlistId: input.wishlistId }
		});
	}

	function trackLeadCreated(input: TrackLeadCreatedInput): void {
		if (!import.meta.client) return;
		const portalUserId = getPortalUserId();
		if (!portalUserId || !input.leadId || !input.propertyId) return;

		syncOfflineBehaviorViews();
		trackPropertyViewed({
			propertyId: input.propertyId,
			property: input.property,
			source: input.source
		});

		const { sessionId } = getTrackingIdentity();
		emitEvent({
			...buildCommonFields(
				'lead_created',
				portalUserId,
				sessionId,
				input.propertyId,
				resolveSnapshot(input.property)
			),
			payload: {
				leadId: input.leadId,
				source: input.source ?? 'property_page'
			}
		});
	}

	/**
	 * Flushes the pre-login property_viewed buffer to the current user.
	 * Remaining views are attributed to whoever logs in on this browser
	 * (intentional shared-device trade-off for the offline buffer).
	 */
	function syncOfflineBehaviorViews(): void {
		if (!import.meta.client) return;
		const portalUserId = getPortalUserId();
		if (!portalUserId) return;

		const views = takeOfflineBehaviorViews();
		if (views.length === 0) return;

		const { sessionId } = getTrackingIdentity();

		for (const view of views) {
			if (hasViewedPropertyThisSession(view.propertyId)) continue;
			markPropertyViewedThisSession(view.propertyId);
			emitEvent(
				{
					...buildCommonFields(
						'property_viewed',
						portalUserId,
						view.sessionId || sessionId,
						view.propertyId,
						view.propertySnapshot,
						view.occurredAt
					),
					payload: {
						viewType: view.viewType,
						source: view.source
					}
				},
				() => unmarkPropertyViewedThisSession(view.propertyId)
			);
		}
	}

	return {
		trackPropertyViewed,
		trackWishlistPropertyAdded,
		trackWishlistPropertyRemoved,
		trackLeadCreated,
		syncOfflineBehaviorViews
	};
}
